// Signup attribution — reads the `ref=`/`utm_*` convention already present on ~30 signup
// CTAs, and carries it through the OTP/OAuth redirect chain as a compact, URL-safe string.
//
// Importable from both client and server components: no `server-only`, no node built-ins
// (base64url uses the Web `btoa`/`atob` + `TextEncoder`/`TextDecoder` APIs, available in both
// the browser and the Next.js server/edge runtimes).
//
// Security: `encodeAttribution`'s output travels through a URL and `decodeAttribution`'s input
// must be treated as attacker-controlled (a user can hand-craft the `attribution` query param).
// Both directions whitelist keys and cap value length — never widen either without re-reading
// this comment.

import { scrubInviteTokenInString } from '@/lib/observability/scrub-invite-token'

// Order is the size-pressure drop order, reversed: `encodeAttribution` drops from the END of
// this list first, so the long-tail campaign keys (`gclid`, `utm_term`, `utm_content`) go before
// the first-touch keys (`landing_path`, `first_touch_at`, `referrer_host`), and `ref`, the one
// the app acts on, goes last.
export const ATTRIBUTION_KEYS = [
  'ref',
  'mode',
  'domain',
  'referrer_host',
  'first_touch_at',
  'landing_path',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'gclid',
] as const

export type AttributionKey = (typeof ATTRIBUTION_KEYS)[number]

export type Attribution = Partial<Record<AttributionKey, string>>

/** Per-value cap — generous for a UTM/ref/hostname, small enough to bound the jsonb column. */
const MAX_VALUE_LENGTH = 200

/** Total encoded-string cap — defense in depth on top of the per-value cap and the fixed
 * 10-key whitelist (worst case is already bounded to roughly this order of magnitude). */
const MAX_ENCODED_LENGTH = 2000

/** The keys a URL (the `/auth/login` query string) may carry. The first-touch keys are NOT here:
 * a link must never be able to forge `referrer_host` / `landing_path` / `first_touch_at`. */
const URL_KEYS = [
  'ref',
  'mode',
  'domain',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'gclid',
] as const satisfies readonly AttributionKey[]

/** The earliest `first_touch_at` accepted: the feature did not exist before it. */
const FIRST_TOUCH_EARLIEST_MS = Date.parse('2026-01-01T00:00:00.000Z')
const ONE_DAY_MS = 24 * 60 * 60 * 1000

const NO_WHITESPACE_OR_CONTROL = /^[^\s\u0000-\u001f\u007f]+$/
// `_` is allowed in a label: real browsers send referrers like `my_app.example.com`.
const HOSTNAME = /^[a-z0-9_](?:[a-z0-9_-]*[a-z0-9_])?(?:\.[a-z0-9_](?:[a-z0-9_-]*[a-z0-9_])?)*$/i
const ISO_INSTANT = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,3})?(?:Z|[+-]\d{2}:\d{2})$/

/**
 * A landing path with its credential removed. `/invite/<token>` and `/claim/<token>` are landings
 * a real visitor arrives on (an invite email, a free-scan claim email) and the token is the whole
 * credential, so neither may reach `localStorage`, `ft=`, a profile or an event. Runs inside the
 * `landing_path` validator, so every way in and out sees the scrubbed path. One scrubber
 * (`scrubInviteTokenInString`) owns the route list; it also covers `/report/<token>`, which
 * first-touch capture excludes outright (`EXCLUDED_PREFIXES`) but a hand-built payload could carry.
 */
export function normaliseLandingPath(path: string): string {
  return scrubInviteTokenInString(path)
}

/**
 * Normalises rather than rejects: an over-long path is truncated to the cap (after the token
 * scrub, never before — cutting first could leave a token prefix behind) so a valid referrer is
 * never lost with it. Every other shape rule still drops the value.
 */
function validLandingPath(value: string): string | null {
  if (!value.startsWith('/') || value.startsWith('//')) return null
  if (!NO_WHITESPACE_OR_CONTROL.test(value)) return null
  // A path only: a query or fragment is where tokens and PII ride.
  if (value.includes('?') || value.includes('#')) return null
  return normaliseLandingPath(value).slice(0, MAX_VALUE_LENGTH)
}

function validFirstTouchAt(value: string): string | null {
  if (!ISO_INSTANT.test(value)) return null
  const at = Date.parse(value)
  if (Number.isNaN(at)) return null
  if (at < FIRST_TOUCH_EARLIEST_MS || at > Date.now() + ONE_DAY_MS) return null
  return value
}

/** A fully-qualified `chatgpt.com.` is the same host: one trailing dot is stripped, not rejected. */
function validReferrerHost(value: string): string | null {
  const host = value.endsWith('.') ? value.slice(0, -1) : value
  return host.length > 0 && host.length <= MAX_VALUE_LENGTH && HOSTNAME.test(host) ? host : null
}

/** One value through its key's rule. Free-text keys are only length-capped; the first-touch keys
 * are validated and a bad one is dropped, never coerced. */
function cleanValue(key: AttributionKey, raw: unknown): string | null {
  if (typeof raw !== 'string' || raw.length === 0) return null
  switch (key) {
    case 'landing_path':
      return validLandingPath(raw)
    case 'first_touch_at':
      return validFirstTouchAt(raw)
    case 'referrer_host':
      return validReferrerHost(raw)
    default:
      return raw.slice(0, MAX_VALUE_LENGTH)
  }
}

/** Picks whitelisted keys from a URL's search params (e.g. the `/auth/login` query string). */
export function readAttribution(params: URLSearchParams): Attribution | null {
  const result: Attribution = {}
  for (const key of URL_KEYS) {
    const value = cleanValue(key, params.get(key))
    if (value !== null) result[key] = value
  }
  return Object.keys(result).length > 0 ? result : null
}

function sanitizeAttribution(attribution: Attribution): Attribution {
  const sanitized: Attribution = {}
  for (const key of ATTRIBUTION_KEYS) {
    const value = cleanValue(key, attribution[key])
    if (value !== null) sanitized[key] = value
  }
  return sanitized
}

/**
 * The one way a reader turns `profiles.signup_attribution` into an `Attribution`. The column is
 * typed `Record<string, string>` but is really whatever jsonb its owner PATCHed ("Users can update
 * own profile", bounded only by `profiles_signup_attribution_size`): a number, an array, non-string
 * values. Keeps only whitelisted keys with valid string values; never throws.
 */
export function sanitizeStoredAttribution(value: unknown): Attribution | null {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return null
  const source = value as Record<string, unknown>
  const result: Attribution = {}
  for (const key of ATTRIBUTION_KEYS) {
    if (!Object.prototype.hasOwnProperty.call(source, key)) continue
    const clean = cleanValue(key, source[key])
    if (clean !== null) result[key] = clean
  }
  return Object.keys(result).length > 0 ? result : null
}

function toBase64Url(input: string): string {
  const bytes = new TextEncoder().encode(input)
  let binary = ''
  for (const byte of bytes) binary += String.fromCharCode(byte)
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

function fromBase64Url(input: string): string {
  const base64 = input.replace(/-/g, '+').replace(/_/g, '/')
  const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4)
  const binary = atob(padded)
  const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0))
  return new TextDecoder().decode(bytes)
}

/**
 * Whitelists + caps `attribution`, then base64url-encodes it. Returns `''` for an empty or
 * all-stripped input — callers should treat that as "nothing to append".
 */
export function encodeAttribution(attribution: Attribution): string {
  const sanitized = sanitizeAttribution(attribution)
  if (Object.keys(sanitized).length === 0) return ''

  // The per-value and total caps are not mutually consistent at the boundary: ten maxed
  // 200-char values base64 to roughly 2.9 KB, past MAX_ENCODED_LENGTH. Dropping the
  // lowest-value keys until it fits beats returning '' and losing the attribution entirely —
  // `ref` is the one we actually act on, so it is dropped last.
  const droppableFirst = [...ATTRIBUTION_KEYS].reverse()
  const trimmed: Attribution = { ...sanitized }
  let encoded = toBase64Url(JSON.stringify(trimmed))

  for (const key of droppableFirst) {
    if (encoded.length <= MAX_ENCODED_LENGTH) break
    if (trimmed[key] === undefined) continue

    delete trimmed[key]
    encoded = Object.keys(trimmed).length > 0 ? toBase64Url(JSON.stringify(trimmed)) : ''
  }

  return encoded.length <= MAX_ENCODED_LENGTH ? encoded : ''
}

/**
 * Decodes an `encodeAttribution` output. The input is assumed attacker-controlled (it travels
 * through a URL): malformed base64, malformed JSON, a non-object payload, unknown keys, and
 * non-string / oversized values are all stripped rather than thrown — this returns `null`
 * instead of throwing on any of them.
 */
export function decodeAttribution(encoded: string): Attribution | null {
  if (!encoded || encoded.length > MAX_ENCODED_LENGTH) return null

  let json: string
  try {
    json = fromBase64Url(encoded)
  } catch {
    return null
  }

  let parsed: unknown
  try {
    parsed = JSON.parse(json)
  } catch {
    return null
  }

  // Same whitelist and per-key validation as every other way in (a forged `ft` / `attribution`).
  return sanitizeStoredAttribution(parsed)
}
